import { selectActiveOrg, useAuthStore } from '../features/auth/store';
import { isDevMode, refreshTokens } from '../features/auth/oidc';

/**
 * Fetch wrapper for the Solid Financials API.
 * - Base URL is always `/api` (vite dev proxy / nginx in prod — never hardcode origin).
 * - Adds `Authorization: Bearer <token>` and `X-Organization: <orgId>` from the auth store.
 * - On 401 (OIDC mode): one silent refresh + retry; if that fails, clears the
 *   store so the router redirects to /login.
 * - Throws ApiError on non-2xx with the server's message when available.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/** Single-flight silent refresh — concurrent 401s share one token request. */
let refreshInFlight: Promise<boolean> | null = null;

function attemptRefresh(): Promise<boolean> {
  const { refreshToken, isAuthenticated } = useAuthStore.getState();
  if (isDevMode() || !isAuthenticated || !refreshToken) return Promise.resolve(false);
  refreshInFlight ??= refreshTokens(refreshToken)
    .then((tokens) => {
      useAuthStore.getState().setTokens(tokens);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function request<T>(path: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
  const state = useAuthStore.getState();
  const { accessToken } = state;
  const activeOrg = selectActiveOrg(state);

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (activeOrg) headers['X-Organization'] = activeOrg.id;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let url = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  if (options.params) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined) qs.set(key, String(value));
    }
    const encoded = qs.toString();
    if (encoded) url += `?${encoded}`;
  }

  const res = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (res.status === 401 && !isRetry && useAuthStore.getState().isAuthenticated) {
    if (await attemptRefresh()) {
      return request<T>(path, options, true);
    }
    // Silent refresh unavailable/failed → session is over; clear the store so
    // RequireAuth sends the user to /login.
    useAuthStore.getState().clear();
  }

  let payload: unknown;
  const text = await res.text();
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    payload = text;
  }

  if (!res.ok) {
    const message =
      payload !== null &&
      typeof payload === 'object' &&
      'message' in (payload as Record<string, unknown>)
        ? String((payload as Record<string, unknown>).message)
        : payload !== null &&
            typeof payload === 'object' &&
            'error' in (payload as Record<string, unknown>)
          ? String((payload as Record<string, unknown>).error)
          : `Request failed with status ${res.status}`;
    throw new ApiError(res.status, message, payload);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
